// The four plugin screenshots a Shopping click sees, directly under the hero's
// one-line pitch.
//
// Ordered as an argument rather than a gallery: the plugin on its own first,
// grid and piano roll, which is what the sentence above it just promised; then
// the same plugin inside a real Logic session, the "in every DAW" claim shown
// rather than asserted; then the two things the ad had no room for.
//
// One order on every screen. An earlier version led with the Logic shot on
// desktop, where it has the pixels to read — but two orders means the visitor
// who checks on a phone and buys on a laptop meets a different first argument
// each time, which is worse than the sharpest possible opening slide.
//
// The first two come from the user's own screenshots; the rest are the manual's,
// re-encoded. All are 1600px WebP renders of public/manual/*.png, which is the
// whole reason this is affordable in a landing page: the PNGs weigh 3.3 MB, these
// weigh 211 KB. Regenerate with:
//   cd public/manual && for f in grid-and-piano-roll inside-logic \
//     sample-editor preset-browser; do
//     cwebp -q 82 -resize 1600 0 "$f.png" -o "shots/$f.webp"; done
//
// `alt` stays English in every locale on purpose. No existing landing key
// describes a screenshot, and inventing four would mean four new strings times
// five locales — the same translation job as visible captions, which we decided
// against for now. English alt beats no alt.
export const SHOTS = [
  { id: "grid-and-piano-roll", alt: "Six drum lanes on a 16-step grid, with the piano roll open underneath" },
  { id: "inside-logic", alt: "TrapMan open over a Logic Pro session" },
  { id: "sample-editor", alt: "The sample editor trimming and shaping a one-shot" },
  { id: "preset-browser", alt: "The preset browser and the TrapMan Core Kit" },
];

// The homepage's set, under the hero for every visitor. The hero itself is the
// grid and piano roll, so these open on what it cannot show: the grid in a
// full beat with the pattern chain, then the three windows a producer opens
// next. The captures come from two sessions, one in warm colours and one in
// pink and blue, and the order alternates them so no two neighbours look alike.
// Cropped from the founder's Logic captures to the plugin alone, rounded, all
// 2000x950 so the track keeps one height from slide to slide (the library shot
// loses the grid's last steps on the right to fit).
export const HOME_SHOTS = [
  { id: "home-grid-pattern-chain", src: "/shots/home-grid-pattern-chain.webp", alt: "A full beat on the step grid, with the pattern chain underneath" },
  { id: "home-sample-editor", src: "/shots/home-sample-editor.webp", alt: "The sample editor trimming and shaping an 808" },
  { id: "home-preset-browser", src: "/shots/home-preset-browser.webp", alt: "The preset browser with kits and patterns" },
  { id: "home-sample-library", src: "/shots/home-sample-library.webp", alt: "The sample library open beside the grid" },
];

export function shotSrc(id) {
  return `/manual/shots/${id}.webp`;
}

// The same screenshots for the ad landings (/ig, /tk, /igbuy), minus the Logic
// session. That slide argues "it runs in your DAW", which is the one claim this
// visitor did not come to check: they just watched a video of the plugin itself,
// and the DAW list is already a bullet a few lines above. Dropping it keeps the
// swipe short enough to finish on a phone.
//
// The hero render is prepended by the page rather than listed here, because its
// alt is the only one with a translation to reuse (`hero.imageAlt`) and this
// file's alts are deliberately English-only.
export const AD_SHOTS = SHOTS.filter((shot) => shot.id !== "inside-logic");

// Arrow and dot navigation. Clamped rather than wrapping: the track is a
// scroll-snap container, so a wrapping arrow would disagree with what a swipe
// does at the same edge.
export function stepIndex(current, delta, length) {
  if (length <= 0) return 0;
  const next = current + delta;
  if (next < 0) return 0;
  if (next > length - 1) return length - 1;
  return next;
}

// Which slide a swipe landed on, from the track's own scroll position. Rounding
// rather than flooring so a snap that stops a pixel short still reads as
// arrived.
export function indexFromScroll(scrollLeft, slideWidth, length) {
  if (slideWidth <= 0 || length <= 0) return 0;
  return stepIndex(0, Math.round(scrollLeft / slideWidth), length);
}
